import type {
    AnchorHTMLAttributes,
    FC,
    MouseEvent,
    ReactNode,
    Ref
} from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Logo } from '@/components/generals/logo'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'

type Phase = 'idle' | 'cover' | 'reveal'

interface TransitionContextValue {
    navigate: (href: string) => void
}

interface PageTransitionProps {
    children: ReactNode
}

interface TransitionLinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
    href: string
    children: ReactNode
    ref?: Ref<HTMLAnchorElement>
}

const COVER_MS = 700
const REVEAL_MS = 900

const TransitionContext = createContext<TransitionContextValue>({
    navigate: (href: string) => {
        window.location.assign(href)
    }
})

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
    window.setTimeout(resolve, ms)
})

const splitHref = (href: string): { path: string, hash: string } => {
    const [path, hash = ''] = href.split('#')

    return { path: path ?? '', hash }
}

export const PageTransition: FC<PageTransitionProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const { lenis, scrollTo } = useSmoothScroll()
    const [phase, setPhase] = useState<Phase>('idle')
    const busy = useRef(false)

    const navigate = useCallback(async (href: string): Promise<void> => {
        if (busy.current) return

        const { hash } = splitHref(href)
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        if (reduce) {
            await router.push(href)

            return
        }

        busy.current = true
        setPhase('cover')
        lenis?.stop()

        await wait(COVER_MS)

        lenis?.start()

        try {
            await router.push(href, undefined, { scroll: false })
        }
        catch {
            // An interrupted navigation still needs the curtain lifted.
        }

        if (hash) {
            scrollTo(`#${hash}`, { immediate: true })
            await wait(140)
            scrollTo(`#${hash}`, { immediate: true })
        }
        else {
            scrollTo(0, { immediate: true })
            await wait(60)
        }

        setPhase('reveal')

        await wait(REVEAL_MS)

        setPhase('idle')
        busy.current = false
    }, [router, lenis, scrollTo])

    const value = useMemo(() => ({ navigate }), [navigate])

    return (
        <TransitionContext.Provider value={value}>
            {children}

            <div aria-hidden className='route-curtain' data-phase={phase}>
                <div className='flex items-center gap-4'>
                    <Logo className='size-12 md:size-16' />

                    <span className='font-heading text-5xl font-extrabold tracking-[-0.05em] md:text-7xl'>
                        Overtone
                    </span>
                </div>
            </div>
        </TransitionContext.Provider>
    )
}

export const TransitionLink: FC<TransitionLinkProps> = ({
    href,
    onClick,
    children,
    ref,
    ...rest
}): ReactNode => {
    const router = useRouter()
    const { navigate } = useContext(TransitionContext)
    const { scrollTo } = useSmoothScroll()

    const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
        onClick?.(event)

        if (event.defaultPrevented) return
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        if (rest.target && rest.target !== '_self') return

        const { path, hash } = splitHref(href)
        const currentPath = router.asPath.split('#')[0].split('?')[0] || '/'
        const targetPath = path === '' ? currentPath : path

        event.preventDefault()

        if (targetPath === currentPath) {
            if (hash) scrollTo(`#${hash}`)
            else scrollTo(0)

            return
        }

        navigate(href)
    }

    return (
        <Link ref={ref} href={href} scroll={false} onClick={handleClick} {...rest}>
            {children}
        </Link>
    )
}
